import config from 'config/config';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { ActionList, ActionRow, Section, SectionHeader, TabShell } from 'components/ds';
import { CommonEmbed } from 'components/common/CommonEmbed';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { userStore } from 'store/userStore';
import { changeLanguage } from 'locales/i18n';
import {
    IcBell, IcBan, IcUser, IcHome, IcPin, IcMegaphone, IcQuestion,
    IcEnvelope, IcClipboard, IcClose, IcCheck, IcDocument, IcGlobe,
} from 'components/common/icons/EmojiIcons';
import styles from './Settings.module.css';

interface DocState {
    open: boolean;
    title: string;
    path: string;
    service?: string;
}

/** 국기 이모지 대신 글자 코드로 — UI 안에는 이모지를 쓰지 않습니다 */
const LANGUAGES = [
    { code: 'ko', short: 'KO', label: '한국어' },
    { code: 'en', short: 'EN', label: 'English' },
    { code: 'ja', short: 'JA', label: '日本語' },
];

/**
 * 설정 탭.
 *
 * 담는 것 (기존 화면과 동일)
 *   알림 · 차단 사용자 / 프로필 · 집 주소 · 내 위치 보기
 *   / 공지 · FAQ · 문의하기 · 문의내역 · 약관 / 언어 / 로그아웃 / 버전
 */
export const Settings: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { showConfirm } = useAlert();
    const [doc, setDoc] = useState<DocState>({ open: false, title: '', path: '' });
    const [langOpen, setLangOpen] = useState(false);
    const currentLang = i18n.language;

    const openDoc = (title: string, path: string, service?: string) =>
        setDoc({ open: true, title, path, service });
    const closeDoc = () => setDoc({ open: false, title: '', path: '', service: undefined });

    // 하드웨어/브라우저 뒤로가기 → 겹쳐 뜬 것만 닫습니다.
    // 없으면 뒤로가기가 /settings 스택을 pop 해 /home 또는 /login 으로 이탈합니다.
    useBackDismiss(doc.open, closeDoc);
    useBackDismiss(langOpen, () => setLangOpen(false));

    const handleLogout = async () => {
        const confirmed = await showConfirm({ message: t('settings.logoutConfirm') });
        if (!confirmed) return;
        userStore.onLogout();
        // 네이티브에선 WebView 를 root 로 reload — 남아 있는 메모리 상태(jotai)까지
        // 정리해 자동 재인증 같은 잔여 동작을 막습니다. 웹은 navigate 로 충분합니다.
        if (window.ReactNativeWebView) {
            window.location.replace('/login');
        } else {
            navigate('/login', { replace: true });
        }
    };

    const currentLangLabel = LANGUAGES.find(l => l.code === currentLang)?.label || currentLang;

    return (
        <TabShell title={t('settings.title')}>
            {/* ══ 알림 ══ */}
            <Section spacing="tight">
                <SectionHeader title={t('settings.sectionAlert')} />
                <ActionList>
                    <ActionRow
                        icon={<IcBell size={20} />}
                        title={t('settings.notification')}
                        onClick={() => navigate('/settings/notifications')}
                    />
                    <ActionRow
                        icon={<IcBan size={20} />}
                        title={t('settings.blockedUsers')}
                        onClick={() => navigate('/settings/blocked-users')}
                    />
                </ActionList>
            </Section>

            {/* ══ 내 정보 ══ */}
            <Section divided>
                <SectionHeader title={t('settings.sectionMe')} />
                <ActionList>
                    <ActionRow
                        icon={<IcUser size={20} />}
                        title={t('settings.profile')}
                        onClick={() => navigate('/settings/profile')}
                    />
                    <ActionRow
                        icon={<IcHome size={20} />}
                        title={t('settings.homeAddress')}
                        onClick={() => navigate('/profile/home-address')}
                    />
                    <ActionRow
                        icon={<IcPin size={20} />}
                        title={t('settings.myLocationViews')}
                        onClick={() => navigate('/settings/my-location-views')}
                    />
                </ActionList>
            </Section>

            {/* ══ 안내 ══ */}
            <Section divided>
                <SectionHeader title={t('settings.sectionHelp')} />
                <ActionList>
                    <ActionRow
                        icon={<IcMegaphone size={20} />}
                        title={t('settings.notice')}
                        onClick={() => openDoc(t('settings.notice'), '/notice', config.SERVICE_ID)}
                    />
                    <ActionRow
                        icon={<IcQuestion size={20} />}
                        title={t('settings.faq')}
                        onClick={() => openDoc(t('settings.faq'), '/faq', config.SERVICE_ID)}
                    />
                    <ActionRow
                        icon={<IcEnvelope size={20} />}
                        title={t('settings.inquiry')}
                        onClick={() => openDoc(t('settings.inquiry'), '/qna/send', config.SERVICE_ID)}
                    />
                    <ActionRow
                        icon={<IcClipboard size={20} />}
                        title={t('settings.inquiryList')}
                        onClick={() => openDoc(t('settings.inquiryList'), '/qna', config.SERVICE_ID)}
                    />
                    <ActionRow
                        icon={<IcDocument size={20} />}
                        title={t('settings.terms')}
                        onClick={() => openDoc(t('settings.terms'), '/terms', config.SERVICE_ID)}
                    />
                </ActionList>
            </Section>

            {/* ══ 앱 ══ */}
            <Section divided last>
                <SectionHeader title={t('settings.sectionApp')} />
                <ActionList>
                    <ActionRow
                        icon={<IcGlobe size={20} />}
                        title={t('settings.language')}
                        value={currentLangLabel}
                        onClick={() => setLangOpen(true)}
                    />
                    <ActionRow
                        title={t('settings.logout')}
                        onClick={handleLogout}
                        danger
                        noArrow
                    />
                </ActionList>
                <p className={styles.version}>{t('settings.version')} 1.0.0</p>
            </Section>

            {/* ── 공지·FAQ 등 내장 문서 ── */}
            {doc.open && (
                <div className={styles.docOverlay}>
                    <div className={styles.docHead}>
                        <h2 className={styles.docTitle}>{doc.title}</h2>
                        <button
                            type="button"
                            className={styles.sheetClose}
                            onClick={closeDoc}
                            aria-label={t('common.close')}
                        >
                            <IcClose size={20} />
                        </button>
                    </div>
                    <div className={styles.docBody}>
                        <CommonEmbed
                            title={doc.title}
                            path={doc.path}
                            showHeader={false}
                            service={doc.service}
                            onClose={closeDoc}
                        />
                    </div>
                </div>
            )}

            {/* ── 언어 고르기 ── */}
            {langOpen && (
                <div className={styles.overlay} onClick={() => setLangOpen(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('language.title')}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('language.title')}</h2>
                            <button
                                type="button"
                                className={styles.sheetClose}
                                onClick={() => setLangOpen(false)}
                                aria-label={t('common.close')}
                            >
                                <IcClose size={20} />
                            </button>
                        </div>
                        <div className={styles.sheetBody}>
                            {LANGUAGES.map(lang => {
                                const on = currentLang === lang.code;
                                return (
                                    <button
                                        key={lang.code}
                                        type="button"
                                        className={on ? `${styles.lang} ${styles.langOn}` : styles.lang}
                                        onClick={() => {
                                            if (!on) changeLanguage(lang.code);
                                            setLangOpen(false);
                                        }}
                                    >
                                        <span className={styles.langCode}>{lang.short}</span>
                                        <span className={styles.langLabel}>{lang.label}</span>
                                        {on ? <span className={styles.langCheck}><IcCheck size={18} /></span> : null}
                                    </button>
                                );
                            })}
                            <p className={styles.sheetNote}>{t('language.change')}</p>
                        </div>
                    </div>
                </div>
            )}
        </TabShell>
    );
};
